import { useMemo, useState } from 'react';
import { CATEGORIES, TRENDS, type TrendItem } from '@/mocks/trending';
import Bars from './Bars';

const RANK_STYLE = [
  'bg-primary-500 text-background-50',
  'bg-primary-300 text-primary-950',
  'bg-accent-400 text-accent-950',
];

function dirIcon(dir: TrendItem['dir']) {
  if (dir === 'up') return { icon: 'ri-arrow-up-line', cls: 'text-accent-600' };
  if (dir === 'down') return { icon: 'ri-arrow-down-line', cls: 'text-primary-500' };
  return { icon: 'ri-arrow-right-line', cls: 'text-foreground-400' };
}

function tagCls(tag: string) {
  if (tag === '爆瓜') return 'bg-primary-100 text-primary-700 border-primary-200';
  if (tag === '热搜爆') return 'bg-accent-100 text-accent-700 border-accent-200';
  if (tag === '热议') return 'bg-secondary-100 text-secondary-800 border-secondary-200';
  return 'bg-background-100 text-foreground-600 border-background-200';
}

export default function HotRank() {
  const [cat, setCat] = useState('all');
  const list = useMemo(() => {
    const base = cat === 'all' ? TRENDS : TRENDS.filter((t) => t.cat === cat);
    return base.slice(0, 10);
  }, [cat]);

  return (
    <section id="hot" className="scroll-mt-24 bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-background-50 px-3 py-1 text-xs font-semibold text-primary-600 border border-primary-200">
              <i className="ri-fire-line" aria-hidden="true"></i>全网热榜 · 实时追踪
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              此刻谁在热搜里<span className="text-primary-500">飙升</span>
            </h2>
            <p className="mt-2 max-w-xl text-sm md:text-base text-foreground-600">
              综合多路公开信号，聚合当前最热话题并按热度、涨跌与趋势排序，每 3 秒自动刷新。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1 md:p-1.5" role="tablist" aria-label="热榜分类">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={cat === c.key}
                onClick={() => setCat(c.key)}
                className={`whitespace-nowrap inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs md:text-sm font-medium transition ${
                  cat === c.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={c.icon} aria-hidden="true"></i>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-7 grid gap-4 md:mt-8">
          {list.map((t) => {
            const idx = t.id - 1;
            const rankBox = idx < 3 ? RANK_STYLE[idx] : 'bg-background-200/80 text-foreground-600';
            const d = dirIcon(t.dir);
            return (
              <article
                key={t.id}
                className="group flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50 p-3 md:flex-row md:items-center md:gap-4 md:p-4 transition hover:border-primary-200 hover:bg-background-50"
              >
                <div className="flex items-center gap-3 md:w-16 shrink-0">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg font-heading text-base font-bold ${rankBox}`}>
                    {String(t.id).padStart(2, '0')}
                  </span>
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-foreground-950 transition group-hover:text-primary-600">
                        {t.title}
                      </h3>
                      <span className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${tagCls(t.tag)}`}>
                        {t.tag}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-500 hidden sm:block">
                      {CATEGORIES.find((c) => c.key === t.cat)?.label} · 综合热度值
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block shrink-0">
                  <Bars data={t.series} tone={t.dir === 'down' ? 'neutral' : 'primary'} />
                </div>
                <div className="flex shrink-0 items-center gap-4 pl-1 md:pl-0 md:justify-end md:w-44">
                  <div className="text-right">
                    <p className="font-heading text-base font-bold text-foreground-950">{t.heat}</p>
                    <p className="text-[11px] text-foreground-500">实时热度</p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ${d.cls}`}
                  >
                    <i className={d.icon} aria-hidden="true"></i>
                    {t.delta}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}